import React from 'react';
import { teamAPI } from '../services/api';
import { isAuthenticatedSync, getAccessTokenAsync } from '../utils/auth';

interface MaydayNotification {
  id: string;
  truppId: number;
  truppName: string;
  triggeredBy: string;
  timestamp: number;
  dismissed: boolean;
}

interface MaydayNotificationManagerProps {
  einsatzId: number;
  trupps: any[];
  onNotificationChange: (notifications: MaydayNotification[]) => void;
}

const MaydayNotificationManager: React.FC<MaydayNotificationManagerProps> = ({ 
  einsatzId, 
  trupps, 
  onNotificationChange 
}) => {
  const [maydayNotifications, setMaydayNotifications] = React.useState<MaydayNotification[]>([]);
  // Ref für aktuellen State – verhindert stale closures in Intervallen/Callbacks
  const notificationsRef = React.useRef<MaydayNotification[]>([]);
  React.useEffect(() => {
    notificationsRef.current = maydayNotifications;
  }, [maydayNotifications]);

  // Mayday-Benachrichtigungen verwalten
  const addMaydayNotification = async (truppId: number, truppName: string, triggeredBy: string) => {
    // Prüfe ob der aktuelle Benutzer derjenige ist, der den Mayday ausgelöst hat
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const currentUserName = currentUser.name || currentUser.username || 'Unbekannter Benutzer';
    
    // Nur Benachrichtigung hinzufügen, wenn es NICHT der aktuelle Benutzer ist
    if (triggeredBy !== currentUserName) {
      setMaydayNotifications(prev => {
        // Prüfe ob bereits eine Notification für diesen Trupp existiert (verhindert Duplikate)
        const alreadyExists = prev.some(n => n.truppId === truppId && !n.dismissed);
        if (alreadyExists) {
          return prev; // Keine Änderung – Duplikat vermeiden
        }
        
        const notification: MaydayNotification = {
          id: `${truppId}-${Date.now()}`,
          truppId,
          truppName,
          triggeredBy,
          timestamp: Date.now(),
          dismissed: false
        };
        
        const newNotifications = [...prev, notification];
        // In localStorage speichern
        localStorage.setItem(`maydayNotifications_${einsatzId}`, JSON.stringify(newNotifications));
        onNotificationChange(newNotifications);
        return newNotifications;
      });
    } 
  };

  const dismissMaydayNotification = (notificationId: string) => {
    setMaydayNotifications(prev => {
      const updatedNotifications = prev.map(n => 
        n.id === notificationId ? { ...n, dismissed: true } : n
      );
      // In localStorage speichern
      localStorage.setItem(`maydayNotifications_${einsatzId}`, JSON.stringify(updatedNotifications));
      onNotificationChange(updatedNotifications);
      return updatedNotifications;
    });
  };

  const removeMaydayNotification = async (truppId: number) => {
    setMaydayNotifications(prev => {
      const updatedNotifications = prev.filter(n => n.truppId !== truppId);
      // In localStorage speichern
      localStorage.setItem(`maydayNotifications_${einsatzId}`, JSON.stringify(updatedNotifications));
      onNotificationChange(updatedNotifications);
      return updatedNotifications;
    });
  };

  // Mayday-Benachrichtigungen basierend auf Team-Mayday-Status erstellen
  const loadMaydayNotificationsFromAPI = async () => {
    const isAuth = await isAuthenticatedSync();
    if (!isAuth || !navigator.onLine || !einsatzId) return;
    
    try {
      // Test: Prüfe ob Teams an API gesendet werden müssen
      const localTeams = trupps.filter(t => t.id >= 1000000);
      
      // Lokale Teams an API senden falls nötig
      if (localTeams.length > 0) {
        for (const localTeam of localTeams) {
          try {
            // Hier müsste die saveTeamToAPI Funktion importiert werden
            // await saveTeamToAPI(localTeam);
          } catch (error) {
            console.error('❌ Fehler beim Senden von Team an API:', localTeam.name, error);
          }
        }
      }
      
      // Debug: Prüfe aktuellen Benutzer
      const currentUserDebug = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Debug: Prüfe Access Token
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        try {
          const payload = JSON.parse(atob(accessToken.split('.')[1]));
        } catch (error) {
          console.error('❌ Fehler beim Decodieren des Tokens:', error);
        }
      }
      
      // Test: Direkter API-Aufruf mit Debug
      let teams;
      try {
        // Verwende den neuen Endpoint für alle Teams des Einsatzes
        const accessToken = await getAccessTokenAsync();
        let orgID = null;
        
        if (accessToken) {
          try {
            const payload = JSON.parse(atob(accessToken.split('.')[1]));
            orgID = payload.orgID;
          } catch (error) {
            console.error('❌ Fehler beim Decodieren des Tokens:', error);
            return;
          }
        }
        
        if (!orgID) {
          console.error('❌ Keine orgID im Token gefunden');
          return;
        }
        
        teams = await teamAPI.getTeamsByOperation(orgID, einsatzId);
        
        // Mayday-Benachrichtigungen für API-Teams prüfen
        // Verwende notificationsRef für aktuellen State (verhindert stale closure)
        teams.forEach((trupp: any) => {
          if (trupp.mayday) {
            const existingNotification = notificationsRef.current.find(n => n.truppId === trupp.id && !n.dismissed);
            if (!existingNotification) {
              // Mayday von API - Benachrichtigung hinzufügen (echten Auslöser verwenden)
              const triggeredBy = trupp.maydayTriggeredBy || 'Unbekannt';
              addMaydayNotification(trupp.id, trupp.name, triggeredBy);
            }
          }
        });
      } catch (error) {
        console.error('❌ Fehler beim Laden der Teams von API:', error);
      }
    } catch (error) {
      console.error('❌ Fehler beim Laden der Mayday-Benachrichtigungen:', error);
    }
  };

  // Mayday-Benachrichtigungen aus localStorage laden
  React.useEffect(() => {
    if (einsatzId) {
      try {
        const savedNotifications = localStorage.getItem(`maydayNotifications_${einsatzId}`);
        if (savedNotifications) {
          const notifications = JSON.parse(savedNotifications);
          setMaydayNotifications(notifications);
          onNotificationChange(notifications);
        }
      } catch (error) {
        console.warn('Fehler beim Laden der Mayday-Benachrichtigungen:', error);
      }
    }
  }, [einsatzId, onNotificationChange]);

  // Mayday-Benachrichtigungen laden wenn online
  React.useEffect(() => {
    if (navigator.onLine && einsatzId) {
      loadMaydayNotificationsFromAPI(); // Mayday-Benachrichtigungen laden
    }
  }, [einsatzId]);

  // Alte Mayday-Benachrichtigungen automatisch löschen und neue von API laden
  React.useEffect(() => {
    const interval = setInterval(async () => {
      // Alte Benachrichtigungen löschen (älter als 1 Stunde)
      const oneHourAgo = Date.now() - (60 * 60 * 1000);
      setMaydayNotifications(prev => {
        const filtered = prev.filter(n => n.timestamp > oneHourAgo);
        onNotificationChange(filtered);
        return filtered;
      });
      
      // Neue Mayday-Benachrichtigungen von API laden
      await loadMaydayNotificationsFromAPI();
    }, 10000); // Alle 10 Sekunden prüfen

    return () => clearInterval(interval);
  }, [einsatzId, onNotificationChange]);

  // Mayday-Status überwachen und Benachrichtigungen verwalten (nur für lokale Teams)
  React.useEffect(() => {
    // Nur für lokale Teams (nicht API-Teams, da diese über loadMaydayNotificationsFromAPI behandelt werden)
    trupps.forEach(trupp => {
      // Verwende notificationsRef für aktuellen State (verhindert stale closure + Endlosschleifen)
      const existingNotification = notificationsRef.current.find(n => n.truppId === trupp.id);
      
      // Prüfe ob es ein lokales Team ist (kleine ID)
      const isLocalTeam = trupp.id < 1000000;
      
      if (trupp.mayday && !existingNotification && isLocalTeam) {
        // Neuer Mayday von lokalem Team - Benachrichtigung hinzufügen
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        const triggeredBy = currentUser.name || currentUser.username || 'Unbekannter Benutzer';
        addMaydayNotification(trupp.id, trupp.name, triggeredBy);
      } else if (!trupp.mayday && existingNotification) {
        // Mayday beendet - Benachrichtigung entfernen
        removeMaydayNotification(trupp.id);
      }
    });
  }, [trupps]); // Nur auf trupps-Änderungen reagieren, nicht auf maydayNotifications

  // Render Mayday-Benachrichtigungen
  return (
    <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 10000 }}>
      {maydayNotifications
        .filter(n => !n.dismissed)
        .map(notification => (
          <div
            key={notification.id}
            style={{
              background: '#c00',
              color: '#fff',
              padding: '12px 16px',
              borderRadius: 8,
              marginBottom: 8,
              maxWidth: 300,
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              animation: 'pulse 2s infinite'
            }}
          >
            <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
              🚨 MAYDAY: {notification.truppName}
            </div>
            <div style={{ fontSize: 14, marginBottom: 8 }}>
              {notification.triggeredBy}
            </div>
            <button
              onClick={() => dismissMaydayNotification(notification.id)}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: '#fff',
                padding: '4px 8px',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12
              }}
            >
              Schliessen
            </button>
          </div>
        ))}
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.7; }
            100% { opacity: 1; }
          }
        `}
      </style>
    </div>
  );
};

export default MaydayNotificationManager;
